import React, { useState, useEffect } from "react";
import { apiClient, authService } from "../services/authService";
import "./Administration.css";

function Administration() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [notification, setNotification] = useState({ type: null, message: "" });

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [enabledFilter, setEnabledFilter] = useState("");

  const [skip, setSkip] = useState(0);
  const limit = 50;

  const [registrationsEnabled, setRegistrationsEnabled] = useState(true);
  const [registrationsLoading, setRegistrationsLoading] = useState(true);
  const [registrationsSaving, setRegistrationsSaving] = useState(false);

  // Exam access modal
  const [accessUser, setAccessUser] = useState(null);
  const [accessExams, setAccessExams] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification({ type: null, message: "" });
    }, 5000);
  };

  useEffect(() => {
    fetchUsers();
  }, [searchTerm, roleFilter, enabledFilter, skip]);

  useEffect(() => {
    const loadRegistrations = async () => {
      try {
        setRegistrationsLoading(true);
        const data = await authService.getRegistrationsSetting();
        setRegistrationsEnabled(!!data.registrations_enabled);
      } catch (error) {
        console.error("Error loading registrations setting:", error);
      } finally {
        setRegistrationsLoading(false);
      }
    };
    loadRegistrations();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString(),
      });

      if (searchTerm) {
        params.append("search", searchTerm);
      }
      if (roleFilter) {
        params.append("role", roleFilter);
      }
      if (enabledFilter !== "") {
        params.append("enabled", enabledFilter);
      }

      const res = await apiClient.get(`/users/?${params.toString()}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch (error) {
      console.error("Error loading users:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Error loading users. Please verify you have administrator permissions.";
      showNotification("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRegistrations = async () => {
    const next = !registrationsEnabled;
    try {
      setRegistrationsSaving(true);
      const data = await authService.updateRegistrationsSetting(next);
      setRegistrationsEnabled(!!data.registrations_enabled);
      showNotification(
        "success",
        next
          ? "New user registrations are now enabled"
          : "New user registrations are now disabled"
      );
    } catch (error) {
      console.error("Error updating registrations setting:", error);
      const errorMessage =
        error.response?.data?.detail || "Error updating registrations setting";
      showNotification("error", errorMessage);
    } finally {
      setRegistrationsSaving(false);
    }
  };

  const handleUpdateUser = async (userId, role, enabled) => {
    try {
      await apiClient.put(`/users/${userId}`, {
        role: role || undefined,
        enabled: enabled !== undefined ? enabled : undefined,
      });

      await fetchUsers();
      showNotification("success", "User updated successfully");
    } catch (error) {
      console.error("Error updating user:", error);
      const errorMessage =
        error.response?.data?.detail || "Error updating user";
      showNotification("error", errorMessage);
    }
  };

  const handleRoleChange = (userId, currentRole, enabled) => {
    const newRole = currentRole === "owner" ? "student" : "owner";
    handleUpdateUser(userId, newRole, enabled);
  };

  const handleEnableToggle = (userId, role, currentEnabled) => {
    handleUpdateUser(userId, role, !currentEnabled);
  };

  const openExamAccess = async (user) => {
    if (user.role === "owner") {
      showNotification(
        "error",
        "Owners already have access to all exams"
      );
      return;
    }
    setAccessUser(user);
    setAccessLoading(true);
    setAccessExams([]);
    try {
      const res = await apiClient.get(`/users/${user.id}/exam-access`);
      setAccessExams(res.data.exams || []);
    } catch (error) {
      console.error("Error loading exam access:", error);
      showNotification(
        "error",
        error.response?.data?.detail || "Error loading exam access"
      );
      setAccessUser(null);
    } finally {
      setAccessLoading(false);
    }
  };

  const closeExamAccess = () => {
    setAccessUser(null);
    setAccessExams([]);
  };

  const toggleExamAccessLocal = (examenId) => {
    setAccessExams((prev) =>
      prev.map((exam) =>
        exam.examen_id === examenId
          ? { ...exam, enabled: !exam.enabled }
          : exam
      )
    );
  };

  const saveExamAccess = async () => {
    if (!accessUser) return;
    try {
      setAccessSaving(true);
      const res = await apiClient.put(`/users/${accessUser.id}/exam-access`, {
        accesses: accessExams.map((exam) => ({
          examen_id: exam.examen_id,
          enabled: exam.enabled,
        })),
      });
      setAccessExams(res.data.exams || []);
      showNotification(
        "success",
        `Exam access updated for ${accessUser.username}`
      );
      closeExamAccess();
    } catch (error) {
      console.error("Error saving exam access:", error);
      showNotification(
        "error",
        error.response?.data?.detail || "Error saving exam access"
      );
    } finally {
      setAccessSaving(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    fetchUsers();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("");
    setEnabledFilter("");
    setSkip(0);
  };

  if (loading && users.length === 0) {
    return (
      <div className="administration-wrapper">
        <div className="administration-loading">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="administration-wrapper">
      {notification.type && (
        <div className={`notification notification-${notification.type}`}>
          <span className="notification-message">{notification.message}</span>
          <button
            className="notification-close"
            onClick={() => setNotification({ type: null, message: "" })}
          >
            ×
          </button>
        </div>
      )}

      <div className="administration-header">
        <h1>Administration</h1>
        <p>Manage users, roles, exam access and system permissions</p>
      </div>

      <div className="administration-settings">
        <div className="settings-card">
          <div className="settings-card-info">
            <h2>New user registrations</h2>
            <p>
              When disabled, visitors cannot create new accounts until you
              enable it again.
            </p>
          </div>
          <button
            type="button"
            className={`btn-toggle-registrations ${
              registrationsEnabled ? "is-enabled" : "is-disabled"
            }`}
            onClick={handleToggleRegistrations}
            disabled={registrationsLoading || registrationsSaving}
            aria-pressed={registrationsEnabled}
          >
            {registrationsLoading || registrationsSaving
              ? "Saving..."
              : registrationsEnabled
                ? "Enabled"
                : "Disabled"}
          </button>
        </div>
      </div>

      <div className="administration-filters">
        <form onSubmit={handleSearch} className="filter-form">
          <div className="filter-group">
            <label>
              <img
                src="/icons/filtrar.png"
                alt="Search"
                className="filter-icon"
              />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="filter-input"
              />
            </label>
          </div>

          <div className="filter-group">
            <label>
              <img
                src="/icons/categoria.png"
                alt="Role"
                className="filter-icon"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All roles</option>
                <option value="owner">Owner</option>
                <option value="student">Student</option>
              </select>
            </label>
          </div>

          <div className="filter-group">
            <label>
              <img
                src="/icons/lista-de-verificacion.png"
                alt="Status"
                className="filter-icon"
              />
              <select
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value)}
                className="filter-select"
              >
                <option value="">All statuses</option>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </label>
          </div>

          <div className="filter-actions">
            <button type="submit" className="btn-filter">
              Search
            </button>
            <button type="button" onClick={clearFilters} className="btn-clear">
              Clear
            </button>
          </div>
        </form>
      </div>

      <div className="administration-content">
        <div className="users-stats">
          <p>
            Total users: <strong>{total}</strong>
          </p>
          <p className="users-stats-hint">
            Students need explicit exam access. Owners can take and edit all
            exams.
          </p>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-users">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr
                    key={user.id}
                    className={!user.enabled ? "disabled-user" : ""}
                  >
                    <td>{user.id}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {user.role}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-badge status-${
                          user.enabled ? "enabled" : "disabled"
                        }`}
                      >
                        {user.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td>
                      <div className="user-actions">
                        {user.role === "student" && (
                          <button
                            type="button"
                            onClick={() => openExamAccess(user)}
                            className="btn-action btn-exam-access"
                            title="Manage exam access"
                          >
                            Exam Access
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            handleRoleChange(user.id, user.role, user.enabled)
                          }
                          className="btn-action btn-role"
                          title={`Change role to ${
                            user.role === "owner" ? "Student" : "Owner"
                          }`}
                        >
                          {user.role === "owner" ? "→ Student" : "→ Owner"}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            handleEnableToggle(user.id, user.role, user.enabled)
                          }
                          className={`btn-action ${
                            user.enabled ? "btn-disable" : "btn-enable"
                          }`}
                          title={user.enabled ? "Disable" : "Enable"}
                        >
                          {user.enabled ? "Disable" : "Enable"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {total > limit && (
          <div className="pagination">
            <button
              type="button"
              onClick={() => setSkip(Math.max(0, skip - limit))}
              disabled={skip === 0}
              className="btn-pagination"
            >
              Previous
            </button>
            <span>
              Page {Math.floor(skip / limit) + 1} of{" "}
              {Math.ceil(total / limit)}
            </span>
            <button
              type="button"
              onClick={() => setSkip(skip + limit)}
              disabled={skip + limit >= total}
              className="btn-pagination"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {accessUser && (
        <div className="exam-access-overlay" role="dialog" aria-modal="true">
          <div className="exam-access-modal">
            <div className="exam-access-modal-header">
              <div>
                <h2>Exam access</h2>
                <p>
                  Student: <strong>{accessUser.username}</strong>
                </p>
              </div>
              <button
                type="button"
                className="exam-access-close"
                onClick={closeExamAccess}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="exam-access-modal-body">
              {accessLoading ? (
                <p className="exam-access-loading">Loading exams...</p>
              ) : accessExams.length === 0 ? (
                <p className="exam-access-empty">No exams available yet.</p>
              ) : (
                <ul className="exam-access-list">
                  {accessExams.map((exam) => (
                    <li key={exam.examen_id} className="exam-access-item">
                      <div className="exam-access-item-info">
                        <span className="exam-access-title">
                          {exam.examen_titulo}
                        </span>
                        {exam.empresa_nombre && (
                          <span className="exam-access-empresa">
                            {exam.empresa_nombre}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className={`btn-toggle-registrations ${
                          exam.enabled ? "is-enabled" : "is-disabled"
                        }`}
                        onClick={() => toggleExamAccessLocal(exam.examen_id)}
                        aria-pressed={exam.enabled}
                      >
                        {exam.enabled ? "Enabled" : "Disabled"}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="exam-access-modal-footer">
              <button
                type="button"
                className="btn-clear"
                onClick={closeExamAccess}
                disabled={accessSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-filter"
                onClick={saveExamAccess}
                disabled={accessLoading || accessSaving || accessExams.length === 0}
              >
                {accessSaving ? "Saving..." : "Save access"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Administration;
